import * as React from 'react';

import { cn } from '@/lib/utils';

import { cardVariants, CARD_BASE_STYLE, CARD_ELEVATED_STYLE } from './card-variants';
import {
  SURFACE_CONTENT_TOP_PADDING,
  SURFACE_FOOTER_PADDING,
  SURFACE_HEADER_PADDING,
  SURFACE_PADDING,
  COLOR_SCALE,
  TYPE_SCALE,
  FONT_WEIGHTS,
  TYPE_LEADING,
  SHADOW_SCALE,
  GAP_SCALE,
  FLEX,
} from './tokens';

type CardVariant = 'surface' | 'elevated' | 'outline' | 'soft' | 'ghost';
type CardRadius = 'sm' | 'md' | 'lg' | 'xl' | 'full';
type CardPadding = 'sm' | 'md' | 'lg' | 'xl';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  radius?: CardRadius;
  interactive?: boolean;
  as?: React.ElementType;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    { className, variant, radius = 'lg', interactive = false, as: Component = 'div', ...props },
    ref
  ) => {
    const baseStyle =
      variant === 'elevated' ? CARD_ELEVATED_STYLE : variant === 'outline' ? {} : CARD_BASE_STYLE;

    return (
      <Component
        ref={ref}
        className={cn(
          cardVariants({ variant, radius, interactive }),
          interactive &&
            variant !== 'outline' &&
            `hover:${SHADOW_SCALE.xl} active:translate-y-[1px] hover:scale-[1.02] transition-transform duration-200`,
          className
        )}
        style={baseStyle}
        {...props}
      />
    );
  }
);
Card.displayName = 'Card';

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: CardPadding;
  as?: React.ElementType;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, padding = 'md', as: Component = 'div', ...props }, ref) => {
    // Safe access since padding is constrained to CardPadding type
    const safePadding = SURFACE_HEADER_PADDING[padding as keyof typeof SURFACE_HEADER_PADDING];
    return (
      <Component
        ref={ref}
        className={cn(`${FLEX.flexCol} ${GAP_SCALE.sm}`, safePadding, className)}
        {...props}
      />
    );
  }
);
CardHeader.displayName = 'CardHeader';

interface CardTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: React.ElementType;
}

const CardTitle = React.forwardRef<HTMLDivElement, CardTitleProps>(
  ({ className, as: Component = 'div', ...props }, ref) => (
    <Component
      ref={ref}
      className={cn(
        `${TYPE_SCALE.lg} ${FONT_WEIGHTS.semibold} ${TYPE_LEADING.tight} ${COLOR_SCALE.foreground}`,
        className
      )}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';

interface CardDescriptionProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: React.ElementType;
}

const CardDescription = React.forwardRef<HTMLDivElement, CardDescriptionProps>(
  ({ className, as: Component = 'div', ...props }, ref) => (
    <Component
      ref={ref}
      className={cn(
        `${TYPE_SCALE.sm} ${TYPE_LEADING.relaxed} ${COLOR_SCALE['foreground-muted']}`,
        className
      )}
      {...props}
    />
  )
);
CardDescription.displayName = 'CardDescription';

interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: 'none' | CardPadding;
  as?: React.ElementType;
}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, padding = 'md', as: Component = 'div', ...props }, ref) => {
    // Safe access since padding is constrained to valid types
    const safePadding = SURFACE_PADDING[padding as keyof typeof SURFACE_PADDING];
    const safeContentPadding =
      SURFACE_CONTENT_TOP_PADDING[padding as keyof typeof SURFACE_CONTENT_TOP_PADDING];

    return (
      <Component
        ref={ref}
        className={cn(
          safePadding,
          padding !== 'none' && [
            'pt-0', // This is intentional for content sections
            safeContentPadding,
          ],
          className
        )}
        {...props}
      />
    );
  }
);
CardContent.displayName = 'CardContent';

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: CardPadding;
  as?: React.ElementType;
}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, padding = 'md', as: Component = 'div', ...props }, ref) => {
    // Safe access since padding is constrained to CardPadding type
    const safePadding = SURFACE_FOOTER_PADDING[padding as keyof typeof SURFACE_FOOTER_PADDING];
    return (
      <Component
        ref={ref}
        className={cn(FLEX.flexItemsCenter, safePadding, className)}
        {...props}
      />
    );
  }
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
